import { Outlet } from "@tanstack/react-router"
import { ColumnDef, createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Avatar, AvatarFallback, AvatarImage } from "design-system/atoms/Avatar"
import { Badge } from "design-system/atoms/Badge"
import { Email } from "design-system/atoms/Email"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { MoreHorizontal } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useEmployeesQuery } from "modules/settings/hooks/useEmployeesQuery"
import { useInvitationsQuery } from "modules/settings/hooks/useInvitationsQuery"

import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeType, EmployeeInviteResponseData, EmployeeResponseData } from "schema"
import { ArchiveEmployee } from "./ArchiveEmployee"
import { InvitationActions } from "./InvitationActions"

type EmployeeRow =
  | (EmployeeResponseData & { kind: "employee" })
  | (EmployeeInviteResponseData & { kind: "invitation" })

const columnHelper = createColumnHelper<Features, EmployeeRow>()

export function EmployeesTable() {
  const { data: currentEmployee } = useAuthEmployeeQuery()
  const isOwner = currentEmployee?.isOwner ?? false

  if (isOwner) {
    return <OwnerEmployeesTable />
  }
  return <EmployeesTableCore invitations={[]} />
}

// Only rendered for owners — the query is guaranteed never to run for non-owners
function OwnerEmployeesTable() {
  const { data: invitations = [] } = useInvitationsQuery()
  return <EmployeesTableCore invitations={invitations} />
}

function EmployeesTableCore({ invitations }: { invitations: EmployeeInviteResponseData[] }) {
  const { t } = useTranslation()

  const { data: employees, status: employeesStatus } = useEmployeesQuery()
  const { data: currentEmployee } = useAuthEmployeeQuery()

  const companies = currentEmployee?.organization.companies ?? []
  const isMultiCompany = companies.length > 1
  const isOwner = currentEmployee?.isOwner ?? false
  const isSuperadmin = currentEmployee?.backofficeType === BackofficeType.Superadmin

  const allRows: EmployeeRow[] = [
    ...(employees ?? []).map((e) => ({ ...e, kind: "employee" as const })),
    ...invitations.map((i) => ({ ...i, kind: "invitation" as const })),
  ]

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("name", {
          header: () => t("settings.team.table.name"),
          cell: (info) => {
            const row = info.row.original

            if (row.kind === "invitation") {
              return (
                <span className="text-muted-foreground italic font-medium">{info.getValue()}</span>
              )
            }

            const employee = row
            return (
              <div className="flex items-center gap-2 font-medium">
                <span>{info.getValue() ?? "—"}</span>
                {!isMultiCompany && employee.isOwner && <Badge variant="outline">{"Owner"}</Badge>}
                {!isMultiCompany && !employee.isOwner && employee.isManager && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="secondary">{"Manager"}</Badge>
                    </TooltipTrigger>
                    <TooltipContent>{t("settings.team.table.fullAccessTooltip")}</TooltipContent>
                  </Tooltip>
                )}
              </div>
            )
          },
        }),
        isMultiCompany &&
          columnHelper.display({
            id: "companies",
            header: () => t("settings.team.table.companies"),
            cell: (info) => {
              const row = info.row.original

              if (row.kind === "invitation") {
                return <span className="text-muted-foreground">{"—"}</span>
              }

              const employee = row
              if (employee.isOwner) return <Badge variant="outline">{"Owner"}</Badge>
              if (employee.isManager) {
                return (
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="secondary">{"Manager"}</Badge>
                    </TooltipTrigger>
                    <TooltipContent>{t("settings.team.table.fullAccessTooltip")}</TooltipContent>
                  </Tooltip>
                )
              }
              if (employee.archivedAt) {
                return <Badge variant="destructive">{t("settings.team.table.archived")}</Badge>
              }

              const companiesList = employee.companies
              const hasCompanies = companiesList.length > 0
              if (!hasCompanies) return <span className="text-muted-foreground">{"—"}</span>

              const MAX_VISIBLE = 5
              const fullList = companiesList.map((c) => c.name).join("\n")
              const visibleCompanies = companiesList.slice(0, MAX_VISIBLE)
              const hasMore = companiesList.length > MAX_VISIBLE

              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center -space-x-2">
                      {visibleCompanies.map((company) => (
                        <Avatar key={company.id} className="h-9 w-9 bg-background border">
                          <AvatarImage src={company.logo ?? undefined} alt={company.name} />
                          <AvatarFallback className="text-xs">
                            {company.name
                              .split(" ")
                              .map((word) => word[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                      {hasMore && (
                        <Avatar className="h-9 w-9 bg-background border">
                          <AvatarFallback className="text-xs bg-background">
                            <MoreHorizontal className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent variant="card" className="max-w-sm" side="left">
                    <div className="text-left whitespace-pre-line font-medium">{fullList}</div>
                  </TooltipContent>
                </Tooltip>
              )
            },
          }),
        columnHelper.accessor("email", {
          header: () => t("settings.team.table.email"),
          cell: (info) => <Email email={info.getValue()} showLink showCopy />,
        }),
        columnHelper.display({
          id: "status",
          header: () => t("settings.team.table.statusHeader"),
          cell: (info) => {
            const row = info.row.original
            if (row.kind === "invitation") {
              return <Badge variant="secondary">{t("settings.team.table.status.pending")}</Badge>
            }
            if (row.archivedAt) {
              return (
                <Badge variant="outline" className="text-muted-foreground">
                  {t("settings.team.table.status.archived")}
                </Badge>
              )
            }
            return <Badge variant="success">{t("settings.team.table.status.active")}</Badge>
          },
        }),
        columnHelper.display({
          id: "date",
          header: () => t("settings.team.table.dateLabel.lastLogin"),
          cell: (info) => {
            const row = info.row.original

            if (row.kind === "invitation") {
              return (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">
                    {t("settings.team.table.dateLabel.invited")}
                  </p>
                  <DateDisplay date={row.createdAt} />
                </div>
              )
            }

            if (row.archivedAt) {
              return (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">
                    {t("settings.team.table.dateLabel.archived")}
                  </p>
                  <DateDisplay date={row.archivedAt} />
                </div>
              )
            }

            return (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">
                  {t("settings.team.table.dateLabel.lastLogin")}
                </p>
                {row.lastLoginAt ? (
                  <DateDisplay date={row.lastLoginAt} />
                ) : (
                  <span className="text-sm text-muted-foreground">
                    {t("settings.team.table.dateLabel.neverLoggedIn")}
                  </span>
                )}
              </div>
            )
          },
          meta: { align: "right" },
        }),
        (isOwner || isSuperadmin) &&
          columnHelper.display({
            id: "actions",
            header: () => null,
            cell: (info) => {
              const row = info.row.original
              if (row.kind === "invitation") {
                return isOwner ? <InvitationActions invitation={row} /> : null
              }
              return (
                <ArchiveEmployee employee={row} isOwner={isOwner} isSuperadmin={isSuperadmin} />
              )
            },
            meta: { className: "w-8", align: "right" },
          }),
      ].filter(Boolean) as ColumnDef<Features, EmployeeRow>[],
    [t, isMultiCompany, isOwner, isSuperadmin],
  )

  const table = useTable({
    data: allRows,
    columns,
    features,
  })

  const isLoading = employeesStatus === "pending"

  function renderRows() {
    if (isLoading) {
      return <TableSkeleton numRows={5} colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (allRows.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("settings.team.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => {
      const original = row.original
      const isArchived = original.kind === "employee" && original.archivedAt !== null

      return (
        <TableRow key={row.id} className={cn(isArchived && "opacity-70 italic")}>
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id} {...cell.column.columnDef.meta}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      )
    })
  }

  return (
    <>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} {...header.column.columnDef.meta}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderRows()}</TableBody>
      </Table>
      <Outlet />
    </>
  )
}
